import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, CalendarDays, ArrowLeft } from 'lucide-react';
import { personalDaysApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function PersonalDayForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    personalDaysApi.balance().then(r => setBalance(r.data)).catch(() => setBalance(null));
  }, []);

  const tomorrow = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error('Selecciona la fecha'); return; }
    setSubmitting(true);
    try {
      await personalDaysApi.create({ date, notes: notes || undefined });
      toast.success('Solicitud de asunto propio enviada. Pendiente de aprobación.');
      navigate('/my-requests');
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Error al enviar');
    } finally { setSubmitting(false); }
  };

  if (balance && !balance.convenio_id) {
    return (
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Atrás</Button>
        <Card>
          <CardContent className="py-10 text-center space-y-3">
            <CalendarDays className="h-10 w-10 mx-auto text-muted-foreground" />
            <h2 className="text-lg font-semibold">No tienes convenio asignado</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Para solicitar asuntos propios primero debes estar asignado a un convenio.
              Contacta con RRHH o tu administrador para que te asigne uno.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" /> Atrás</Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" />
            Solicitar Asunto Propio
          </CardTitle>
        </CardHeader>
        <CardContent>
          {balance && (
            <div className="bg-muted/40 rounded-lg p-4 mb-4 space-y-1">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Tu convenio</div>
              <div className="font-semibold">{balance.convenio_name}</div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                <div>
                  <div className="text-2xl font-bold text-primary">{balance.available}</div>
                  <div className="text-xs text-muted-foreground">Disponibles</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{balance.used}</div>
                  <div className="text-xs text-muted-foreground">Usados</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-600">{balance.pending}</div>
                  <div className="text-xs text-muted-foreground">Pendientes</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Total anual: {balance.total} día{balance.total !== 1 ? 's' : ''} · año {balance.year}. Los días no usados se pierden el 31/12.
              </p>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Fecha *</Label>
              <Input
                type="date" required
                value={date}
                min={tomorrow}
                onChange={e => setDate(e.target.value)}
                data-testid="personal-day-date"
              />
              <p className="text-xs text-muted-foreground">Solo días enteros, laborables, y desde mañana en adelante.</p>
            </div>

            <div className="space-y-1.5">
              <Label>Motivo / notas (opcional)</Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                placeholder="Trámite personal, cita médica, etc."
                data-testid="personal-day-notes"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || !date || (balance && balance.available <= 0)}
              className="w-full"
              data-testid="personal-day-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Solicitar asunto propio
            </Button>
            {balance && balance.available <= 0 && (
              <p className="text-sm text-destructive text-center">
                Sin saldo disponible este año.
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
